/**
 * ARGOVBadge — Inline governance indicator badges
 *
 * NAV-X6: Applied wherever ARGOV context is materially relevant to the user.
 *
 * Truthfulness invariant: These badges indicate the ARGOV governance model
 * classification for an action or data surface. They must not be applied
 * decoratively or speculatively.
 *
 * Badge types:
 *   AI_GOVERNED        — action is AI-generated and subject to ARGOV chain
 *   APPROVAL_REQUIRED  — human approval is required before execution
 *   POLICY_PASSED      — AI policy gate has been passed for this action/context
 *   AUTHORITY_ADVISORY — AI output is advisory only; no automatic execution
 *   DATA_ACCESS_APPROVED — data access has passed governance review
 *   EVIDENCE_AVAILABLE — audit evidence exists for this action/context
 */
import React from "react";
import { ShieldCheck, CircleAlert, CheckCircle2, Eye, Database, FileSearch } from "lucide-react";

const BADGE_CONFIG = {
  AI_GOVERNED: {
    label: "AI GOVERNED",
    icon: ShieldCheck,
    className: "bg-blue-50 text-blue-700 border-blue-200",
    iconClass: "text-blue-500",
  },
  APPROVAL_REQUIRED: {
    label: "APPROVAL REQUIRED",
    icon: CircleAlert,
    className: "bg-amber-50 text-amber-700 border-amber-200",
    iconClass: "text-amber-500",
  },
  POLICY_PASSED: {
    label: "POLICY PASSED",
    icon: CheckCircle2,
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    iconClass: "text-emerald-500",
  },
  AUTHORITY_ADVISORY: {
    label: "AUTHORITY: ADVISORY",
    icon: Eye,
    className: "bg-purple-50 text-purple-700 border-purple-200",
    iconClass: "text-purple-500",
  },
  DATA_ACCESS_APPROVED: {
    label: "DATA ACCESS: APPROVED",
    icon: Database,
    className: "bg-teal-50 text-teal-700 border-teal-200",
    iconClass: "text-teal-500",
  },
  EVIDENCE_AVAILABLE: {
    label: "EVIDENCE AVAILABLE",
    icon: FileSearch,
    className: "bg-slate-50 text-slate-700 border-slate-200",
    iconClass: "text-slate-500",
  },
};

/**
 * ARGOVBadge — inline badge variant (use in headers, cards, action buttons)
 *
 * @param {string} type - one of the badge types above
 * @param {string} [className] - additional CSS classes
 */
export function ARGOVBadge({ type, className = "" }) {
  const cfg = BADGE_CONFIG[type];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`argov-badge-${type.toLowerCase().replace(/_/g, "-")}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold tracking-wider uppercase select-none ${cfg.className} ${className}`}
    >
      <Icon className={`h-3 w-3 shrink-0 ${cfg.iconClass}`} />
      {cfg.label}
    </span>
  );
}

/**
 * ARGOVBadgeStrip — horizontal strip of multiple badges (use in page headers)
 */
export function ARGOVBadgeStrip({ badges = [], className = "" }) {
  if (!badges.length) return null;
  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      data-testid="argov-badge-strip"
    >
      {badges.map((type) => (
        <ARGOVBadge key={type} type={type} />
      ))}
    </div>
  );
}
